import { jsx as _jsx } from "react/jsx-runtime";
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, split, } from "@apollo/client";
import { ApolloLink, Observable, } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { getMainDefinition } from "@apollo/client/utilities";
import { useAuth, useUser } from "@clerk/react";
import { superjsonSerialize } from "@repo/utils";
import { print } from "graphql";
import { createClient, } from "graphql-sse";
import { useEffect, useMemo } from "react";
import possibleTypes from "../gql/client/possibleTypes";
import { customScalarsLink } from "../gql/custom-scalars";
const client = new ApolloClient({
    cache: new InMemoryCache({
        possibleTypes: possibleTypes.possibleTypes,
        typePolicies: {
            CurrencyAmount: {
                merge: true,
            },
            CurrencycloudFundingAccount: {
                keyFields: ["id", "currency"],
            },
            Query: {
                fields: {
                    externalBankAccountDataInstitutions: {
                        keyArgs: (args) => serializeKeyArgs({
                            filter: args?.filter,
                            distinct: args?.distinct,
                        }),
                        merge: paginatedMergeFunction("institutions"),
                    },
                    payouts: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter, order: args?.order }),
                        merge: paginatedMergeFunction("payouts"),
                    },
                    transactions: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter, order: args?.order }),
                        merge: paginatedMergeFunction("transactions"),
                    },
                    transferGroups: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter, order: args?.order }),
                        merge: paginatedMergeFunction("transferGroups"),
                    },
                    transfers: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter, order: args?.order }),
                        merge: paginatedMergeFunction("transfers"),
                    },
                    beneficiaryAccounts: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
                        merge: paginatedMergeFunction("beneficiaryAccounts"),
                    },
                    assetRules: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
                        merge: paginatedMergeFunction("assetRules"),
                    },
                    events: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
                        merge: paginatedMergeFunction("events"),
                    },
                    statementBatches: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("statementBatches"),
                    },
                    statements: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("statements"),
                    },
                    transactionMonitoringFindings: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("transactionMonitoringFindings"),
                    },
                    transactionMonitoringEvaluations: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("transactionMonitoringEvaluations"),
                    },
                    ledgerEntries: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("ledgerEntries"),
                    },
                    calastoneTransactions: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
                        merge: paginatedMergeFunction("calastoneTransactions"),
                    },
                    importFiles: {
                        keyArgs: ["order", "kinds"],
                        merge: paginatedMergeFunction("importFiles"),
                    },
                    externalTransactionMatchProposals: {
                        keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
                        merge: paginatedMergeFunction("externalTransactionMatchProposals"),
                    },
                    termDepositQueue: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("items"),
                    },
                    assetCashflowReports: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("items"),
                    },
                    logos: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("logos"),
                    },
                    stripePayoutQueueItems: {
                        keyArgs: ["filter"],
                        merge: paginatedMergeFunction("stripePayoutQueueItems"),
                    },
                },
            },
            TopUpAssetRule: {
                fields: {
                    history: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("history"),
                    },
                },
            },
            AlertAssetRule: {
                fields: {
                    history: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("history"),
                    },
                },
            },
            RecurringWithdrawalAssetRule: {
                fields: {
                    history: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("history"),
                    },
                },
            },
            RecurringTransferAssetRule: {
                fields: {
                    history: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("history"),
                    },
                },
            },
            SweepAssetRule: {
                fields: {
                    history: {
                        keyArgs: [],
                        merge: paginatedMergeFunction("history"),
                    },
                },
            },
        },
    }),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "cache-and-network",
            nextFetchPolicy: "cache-first",
            errorPolicy: "all",
        },
    },
});
function paginatedMergeFunction(itemsKey) {
    return function merge(existing, incoming) {
        if (!existing || !incoming || !existing[itemsKey]) {
            return incoming;
        }
        const existingRefs = new Set(existing[itemsKey].map((i) => i.__ref));
        const newItems = incoming[itemsKey].filter((i) => !existingRefs.has(i.__ref));
        return {
            ...existing,
            ...incoming,
            [itemsKey]: [...existing[itemsKey], ...newItems],
        };
    };
}
function serializeKeyArgs(args) {
    // don't use superjsonStringify directly because it returns an object like { "json": ..., "meta": ... }
    return JSON.stringify(superjsonSerialize(args).json);
}
class SSELink extends ApolloLink {
    client;
    operationsByRequest = new WeakMap();
    constructor(options) {
        super();
        this.client = createClient({
            ...options,
            headers: async (request) => {
                const operation = this.operationsByRequest.get(request);
                return {
                    ...(typeof options.headers === "function"
                        ? await options.headers(request)
                        : options.headers),
                    ...operation.getContext().headers,
                };
            },
        });
    }
    request(operation) {
        const request = {
            ...operation,
            query: (operation.getContext().http?.includeQuery ?? true)
                ? print(operation.query)
                : undefined,
        };
        this.operationsByRequest.set(request, operation);
        return new Observable((sink) => {
            return this.client.subscribe(request, {
                next: (result) => sink.next(result),
                complete: () => sink.complete(),
                error: (error) => sink.error(error),
            });
        });
    }
}
export function ApolloWrapper({ children, baseUrl, links, }) {
    const auth = useAuth();
    const user = useUser();
    const sseLink = useMemo(() => new SSELink({
        url: baseUrl,
    }), [baseUrl]);
    const httpLink = useMemo(() => new HttpLink({
        uri: baseUrl,
        credentials: "same-origin",
    }), [baseUrl]);
    const authLink = setContext(async (_, { headers }) => {
        const token = await auth.getToken();
        return {
            headers: {
                ...headers,
                Authorization: token ? `Bearer ${token}` : "",
            },
        };
    });
    client.setLink(ApolloLink.from([
        authLink,
        customScalarsLink,
        ...links,
        (operation, forward) => {
            operation.setContext(({ headers }) => ({
                headers: {
                    "X-Page-Origin": window.location.pathname,
                    ...headers,
                },
            }));
            return forward(operation);
        },
        split(({ query }) => {
            const definition = getMainDefinition(query);
            return (definition.kind === "OperationDefinition" &&
                definition.operation === "subscription");
        }, sseLink, httpLink),
    ]));
    useEffect(() => {
        if (!user.isLoaded) {
            return;
        }
        if (!user.isSignedIn) {
            void client.clearStore();
        }
    }, [user.isLoaded, user.isSignedIn]);
    return _jsx(ApolloProvider, { client: client, children: children });
}
